import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }
  flyToCart() {
    this.#animateFlyToCart(); // вызов уже существующего приватного метода
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    this.animateAddToCart();

    if (!event.target.closest('.quick-add-modal')) this.#animateFlyToCart();
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {IntersectionObserver | undefined} */
  #stickyAtcObserver;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    const personalizationToggle = this.querySelector('input[name="toggle_personalization"]');
    personalizationToggle?.addEventListener('change', this.#syncPersonalizationFields, { signal });
    this.#syncPersonalizationFields();

    this.querySelectorAll('input[name="properties[Uszyj na]"]').forEach((input) => {
      input.addEventListener('change', this.#syncHiddenCustomFields, { signal });
    });
    this.addEventListener('click', this.#onSubmitIntent, { signal, capture: true });
    this.addEventListener('change', this.#onCustomOptionChange, { signal });
    this.addEventListener('input', this.#onCustomOptionChange, { signal });
    this.#syncHiddenCustomFields();
    this.#initPersonalizationPreviews();
    this.#initStickyAtcBar();
  }

  #onSubmitIntent = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!target.closest('button[type="submit"], .add-to-cart-button, [name="add"]')) return;
    this.#syncHiddenCustomFields();
  };

  /**
   * Hidden required fields (display:none) still fail native validation.
   * Disable them so they are skipped in quick-add and on the product page.
   */
  #syncHiddenCustomFields = () => {
    const root = this.querySelector('.product__custom_options');
    if (!root) return;

    root.querySelectorAll('input, select, textarea').forEach((field) => {
      if (
        !(
          field instanceof HTMLInputElement ||
          field instanceof HTMLSelectElement ||
          field instanceof HTMLTextAreaElement
        )
      ) {
        return;
      }

      if (field.classList.contains('product__custom_options_tabs_input')) return;
      if (field.closest('dialog, .bra-size-calculator, .product__custom_personalization_content')) return;

      let hidden = false;
      let ancestor = field.parentElement;
      while (ancestor && ancestor !== root) {
        const style = window.getComputedStyle(ancestor);
        if (style.display === 'none' || style.visibility === 'hidden') {
          hidden = true;
          break;
        }
        ancestor = ancestor.parentElement;
      }

      field.disabled = hidden;
    });
  };

  #getPersonalizationFields() {
    const content = this.querySelector('.product__custom_personalization_content');
    if (!content) return [];

    return Array.from(content.querySelectorAll('input, select, textarea'));
  }

  #isPersonalizationEnabled() {
    return this.querySelector('input[name="toggle_personalization"]')?.checked ?? false;
  }

  #syncPersonalizationFields = () => {
    const enabled = this.#isPersonalizationEnabled();
    const fields = this.#getPersonalizationFields();

    fields.forEach((field) => {
      /** @type {HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement} */ (field).disabled = !enabled;

      if (!enabled) {
        if (field instanceof HTMLInputElement && (field.type === 'radio' || field.type === 'checkbox')) {
          field.checked = false;
        } else if (field instanceof HTMLSelectElement) {
          const firstOption = field.querySelector('option:not([disabled]):not([value=""])');
          field.value = firstOption instanceof HTMLOptionElement ? firstOption.value : '';
        } else if (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement) {
          field.value = '';
        }
      }
    });

    const koronkiSelect = document.getElementById('koronki-color-select');
    const koronkiBlocks = document.querySelectorAll('.koronki__block');

    if (koronkiSelect instanceof HTMLSelectElement && koronkiBlocks.length) {
      const selectedValue = enabled ? koronkiSelect.value : null;
      koronkiBlocks.forEach((block) => {
        if (!(block instanceof HTMLElement)) return;
        block.style.display = selectedValue && block.dataset.block === selectedValue ? 'grid' : 'none';
      });
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#stickyAtcObserver?.disconnect();
    document.documentElement.classList.remove('has-sticky-atc-bar');
    this.#abortController.abort();
  }

  #initStickyAtcBar() {
    if (this.closest('quick-add-component')) return;

    const bar = this.querySelector('[ref="stickyAtcBar"]');
    const original = this.querySelector('[ref="addToCartButton"]');
    const stickyButton = bar?.querySelector('.sticky-atc-bar__button');
    if (!(bar instanceof HTMLElement) || !(original instanceof HTMLButtonElement) || !(stickyButton instanceof HTMLButtonElement)) {
      return;
    }

    const { signal } = this.#abortController;

    const setVisible = (visible) => {
      bar.classList.toggle('is-visible', visible);
      bar.setAttribute('aria-hidden', String(!visible));
      document.documentElement.classList.toggle('has-sticky-atc-bar', visible);
      liftChatWidgets(visible);
    };

    const liftChatWidgets = (visible) => {
      const offset = visible ? getComputedStyle(document.documentElement).getPropertyValue('--sticky-atc-bar-height').trim() || '70px' : '';
      const viewportHeight = Math.min(window.innerHeight, document.documentElement.clientHeight || window.innerHeight);

      const nodes = [
        ...document.querySelectorAll(
          '#shopify-chat, shopify-chat, #ShopifyChat, iframe#dummy-chat-button-iframe, iframe[name="chat-button"], iframe[title*="hat"], iframe[title*="Chat"]'
        ),
        ...document.querySelectorAll('iframe'),
      ];

      const seen = new Set();
      nodes.forEach((el) => {
        if (!(el instanceof HTMLElement) || seen.has(el)) return;
        seen.add(el);
        if (el.closest('.sticky-atc-bar, .callback-popup')) return;

        const style = getComputedStyle(el);
        if (style.position !== 'fixed') return;

        const rect = el.getBoundingClientRect();
        const looksLikeChatLauncher =
          rect.width <= 88 &&
          rect.height <= 88 &&
          rect.width >= 32 &&
          rect.height >= 32 &&
          rect.left < 96 &&
          rect.bottom > viewportHeight - 140;

        const namedChat = /chat|inbox|messenger|czat/i.test(
          `${el.id} ${el.getAttribute('name') || ''} ${el.getAttribute('title') || ''} ${el.tagName}`
        );

        if (!looksLikeChatLauncher && !namedChat && el.id !== 'shopify-chat' && el.tagName !== 'SHOPIFY-CHAT') {
          return;
        }

        if (offset) {
          el.style.setProperty('bottom', offset, 'important');
          el.style.setProperty('transition', 'bottom 0.2s ease', 'important');
        } else {
          el.style.removeProperty('bottom');
          el.style.removeProperty('transition');
        }
      });
    };

    const chatObserver = new MutationObserver(() => liftChatWidgets(bar.classList.contains('is-visible')));
    chatObserver.observe(document.body, { childList: true, subtree: true });
    signal.addEventListener('abort', () => chatObserver.disconnect());

    const update = () => {
      const rect = original.getBoundingClientRect();
      const viewportHeight = Math.min(
        window.innerHeight,
        document.documentElement.clientHeight || window.innerHeight
      );
      const inView = rect.top < viewportHeight - 24 && rect.bottom > 72;
      setVisible(!inView);
    };

    stickyButton.addEventListener(
      'click',
      () => {
        if (original.disabled) return;
        original.click();
      },
      { signal }
    );

    window.addEventListener('scroll', update, { passive: true, signal });
    window.addEventListener('resize', update, { signal });
    this.#stickyAtcObserver = new IntersectionObserver(update, { threshold: [0, 0.25, 0.6, 1] });
    this.#stickyAtcObserver.observe(original);
    update();
  }

  /**
   * @param {Event} event
   */
  #initPersonalizationPreviews() {
    const { signal } = this.#abortController;
    const sections = this.querySelectorAll('.product__custom_personalization_content_block');

    const measureSection = (section) => {
      const button = section.querySelector('.product__custom_personalization_more');
      if (!(button instanceof HTMLButtonElement)) return;
      if (section.classList.contains('is-expanded')) return;

      const grids = [...section.querySelectorAll('.product__custom_personalization_block')].filter(
        (grid) => getComputedStyle(grid).display !== 'none'
      );

      grids.forEach((grid) => {
        const columns = getComputedStyle(grid).gridTemplateColumns.split(' ').filter(Boolean).length;
        const items = [...grid.querySelectorAll('.product__custom_personalization_block_item')];
        const firstRow = items.slice(0, columns);
        if (!firstRow.length) return;

        const rowHeight = Math.max(...firstRow.map((item) => item.offsetHeight));
        const gap = Number.parseFloat(getComputedStyle(grid).rowGap) || 10;
        if (!rowHeight) return;

        grid.style.setProperty('--pers-preview-height', `${rowHeight + gap + rowHeight * 0.38}px`);
      });

      const overflow = grids.some((grid) => {
        const columns = getComputedStyle(grid).gridTemplateColumns.split(' ').filter(Boolean).length;
        const items = grid.querySelectorAll('.product__custom_personalization_block_item').length;
        if (items <= columns) return false;
        return grid.scrollHeight > grid.clientHeight + 8;
      });
      section.classList.toggle('has-overflow', overflow);
      button.hidden = !overflow;
    };

    sections.forEach((section) => {
      const button = section.querySelector('.product__custom_personalization_more');
      if (!(button instanceof HTMLButtonElement)) return;

      button.addEventListener(
        'click',
        () => {
          const expanded = section.classList.toggle('is-expanded');
          button.setAttribute('aria-expanded', String(expanded));
          const label = button.querySelector('.product__custom_personalization_more-label');
          if (label) label.textContent = expanded ? 'Pokaż mniej' : 'Pokaż więcej';
          if (!expanded) requestAnimationFrame(() => measureSection(section));
        },
        { signal }
      );

      section.querySelectorAll('img').forEach((img) => {
        img.addEventListener('load', () => measureSection(section), { signal });
      });

      requestAnimationFrame(() => measureSection(section));
    });

    window.addEventListener(
      'resize',
      () => {
        sections.forEach((section) => {
          if (!section.classList.contains('is-expanded')) measureSection(section);
        });
      },
      { signal }
    );

    this.querySelector('#koronki-color-select')?.addEventListener(
      'change',
      () => {
        sections.forEach((section) => {
          section.classList.remove('is-expanded');
          const button = section.querySelector('.product__custom_personalization_more');
          const label = button?.querySelector('.product__custom_personalization_more-label');
          if (button instanceof HTMLButtonElement) button.setAttribute('aria-expanded', 'false');
          if (label) label.textContent = 'Pokaż więcej';
          requestAnimationFrame(() => measureSection(section));
        });
      },
      { signal }
    );
  }

  #onCustomOptionChange = (event) => {
    const target = event.target;
    if (
      !(
        target instanceof HTMLInputElement ||
        target instanceof HTMLSelectElement ||
        target instanceof HTMLTextAreaElement
      )
    ) {
      return;
    }

    this.#clearFieldValidation(target);
  };

  /**
   * @param {HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement} field
   */
  #getFieldGroup(field) {
    return (
      field.closest('.product__custom_options_blocks_item') ||
      field.closest('.product__custom_input_wrapper') ||
      field.closest('.individual_size__block') ||
      field.closest('fieldset') ||
      field.closest('.variant-option')
    );
  }

  /**
   * @param {HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement} field
   */
  #messageForField(field) {
    const name = field.getAttribute('name') || '';

    if (name.includes('Rozmiar') || field.closest('[data-size]')) {
      return 'Wybierz rozmiar';
    }

    if (field instanceof HTMLInputElement && field.type === 'number') {
      return 'Uzupełnij wymiar';
    }

    if (field instanceof HTMLSelectElement) {
      return 'Wybierz opcję';
    }

    return 'To pole jest wymagane';
  }

  /**
   * @param {Element} group
   */
  #ensureErrorEl(group) {
    let errorEl = group.querySelector('.product__custom_option-error');
    if (!(errorEl instanceof HTMLElement)) {
      errorEl = document.createElement('p');
      errorEl.className = 'product__custom_option-error';
      errorEl.setAttribute('role', 'alert');
    }

    const legend = group.querySelector(':scope > legend');
    const anchor =
      legend ||
      group.querySelector('.variant-option__select-wrapper') ||
      group.querySelector('.product__custom_input') ||
      group.querySelector('.individual_size__block_title');

    if (legend) {
      if (errorEl.parentElement !== legend) legend.append(errorEl);
    } else if (anchor) {
      if (errorEl.previousElementSibling !== anchor) anchor.after(errorEl);
    } else if (errorEl.parentElement !== group) {
      group.append(errorEl);
    }

    return errorEl;
  }

  /**
   * @param {string} message
   */
  #setAddToCartError(message) {
    const { addToCartTextError } = this.refs;
    if (!addToCartTextError) return;

    addToCartTextError.classList.remove('hidden');
    const textNode = addToCartTextError.childNodes[2];
    if (textNode) {
      textNode.textContent = message;
    } else {
      addToCartTextError.appendChild(document.createTextNode(message));
    }
    this.#setLiveRegionText(message);
  }

  #clearAddToCartError() {
    const { addToCartTextError } = this.refs;
    if (addToCartTextError) addToCartTextError.classList.add('hidden');
    this.#clearLiveRegionText();
  }

  /**
   * @param {HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement} field
   */
  #clearFieldValidation(field) {
    field.removeAttribute('aria-invalid');
    const group = this.#getFieldGroup(field);
    if (!group) return;

    group.classList.remove('is-invalid');
    const errorEl = group.querySelector('.product__custom_option-error');
    if (errorEl) errorEl.textContent = '';

    const remaining = this.#getInvalidRequiredFields();
    if (!remaining.length) this.#clearAddToCartError();
  }

  /**
   * @returns {(HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement)[]}
   */
  #getInvalidRequiredFields() {
    const form = this.querySelector('form');
    if (!form) return [];

    /** @type {Set<string>} */
    const seenRadioNames = new Set();
    /** @type {(HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement)[]} */
    const invalid = [];

    form.querySelectorAll('input, select, textarea').forEach((field) => {
      if (
        !(
          field instanceof HTMLInputElement ||
          field instanceof HTMLSelectElement ||
          field instanceof HTMLTextAreaElement
        )
      ) {
        return;
      }

      if (field.disabled || field.hidden) return;
      if (field.classList.contains('product__custom_options_tabs_input')) return;
      if (field.closest('dialog, .bra-size-calculator, .product__custom_personalization_content')) return;
      if (field.type === 'hidden') return;
      if (!field.willValidate) return;
      if (field.checkValidity()) return;

      if (field instanceof HTMLInputElement && field.type === 'radio') {
        if (seenRadioNames.has(field.name)) return;
        seenRadioNames.add(field.name);
      }

      invalid.push(field);
    });

    return invalid;
  }

  /**
   * Native HTML5 bubbles are invisible on custom size radios (opacity: 0).
   * @returns {boolean}
   */
  #validateCustomOptions() {
    this.#syncHiddenCustomFields();

    const invalidFields = this.#getInvalidRequiredFields();
    if (!invalidFields.length) {
      this.#clearAddToCartError();
      return true;
    }

    invalidFields.forEach((field) => {
      field.setAttribute('aria-invalid', 'true');
      const group = this.#getFieldGroup(field);
      if (!group) return;

      group.classList.add('is-invalid');
      const errorEl = this.#ensureErrorEl(group);
      errorEl.textContent = this.#messageForField(field);
    });

    const firstField = invalidFields[0];
    const firstGroup = this.#getFieldGroup(firstField) || firstField;
    const header = document.querySelector('#header-group') || document.querySelector('header-component');
    const headerOffset = header instanceof HTMLElement ? header.getBoundingClientRect().height : 80;
    const top = firstGroup.getBoundingClientRect().top + window.scrollY - headerOffset - 16;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });

    const hasSizeError = invalidFields.some(
      (field) => (field.getAttribute('name') || '').includes('Rozmiar') || Boolean(field.closest('[data-size]'))
    );
    this.#setAddToCartError(
      hasSizeError ? 'Wybierz rozmiar, aby dodać produkt do koszyka' : 'Uzupełnij wymagane pola, aby dodać produkt do koszyka'
    );

    return false;
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  async handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    if (this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute('disabled') === 'true') return;

    this.#syncPersonalizationFields();

    if (!this.#validateCustomOptions()) return;

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    const formData = new FormData(form);



    //  Добавляем основной товар
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.root + 'cart/add.js', {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
    .then((response) => response.json())
    .then((response) => {
      if (response.status) {
        // Обработка ошибки
        window.dispatchEvent(new CartErrorEvent(this.id, response.message));
        if (addToCartTextError) {
          addToCartTextError.classList.remove("hidden");
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            addToCartTextError.appendChild(document.createTextNode(response.message));
          }
          this.#setLiveRegionText(response.message);
          this.#timeout = setTimeout(() => {
            addToCartTextError.classList.add("hidden");
            this.#clearLiveRegionText();
          }, 10000);
        }
        this.dispatchEvent(new CartAddEvent({}, this.id, { didError: true, source: "product-form-component", itemCount: Number(formData.get("quantity")), productId: this.dataset.productId }));
      } else {
        // ✅ Успешное добавление товара
        if (addToCartTextError) addToCartTextError.classList.add("hidden");

        if (this.refs.addToCartButtonContainer) {
          this.refs.addToCartButtonContainer.animateAddToCart();
          this.refs.addToCartButtonContainer.flyToCart();
        }

        const id = formData.get("id");
        this.dispatchEvent(new CartAddEvent({}, id.toString(), {
          source: "product-form-component",
          itemCount: Number(formData.get("quantity")),
          productId: this.dataset.productId,
          sections: response.sections
        }));
      }
    })
    .catch(console.error)
    .finally(() => {
      cartPerformance.measureFromEvent('add:user-action', event);
    });
  }

  async applyDiscountCodes(codes) {
    if (!codes.length) return;

    await fetch(Theme.routes.cart_update_url || '/cart/update.js', {
      method: 'POST',
      body: JSON.stringify({
        discount: codes.join(','),
      }),
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });
  }


  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    if (!currentAddToCartButton) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }

    // Update the add to cart button text and icon
    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    // Update the variant ID
    variantId.value = event.detail.resource.id ?? '';

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
document.addEventListener('DOMContentLoaded', function() {
  const optionsContainer = document.querySelector('.product__custom_options_blocks');
  const checkoutBlock = document.querySelector('.accelerated-checkout-block');
  if (!optionsContainer || !checkoutBlock) return;

  const checkoutBlockButton = checkoutBlock.querySelector('.shopify-payment-button');
  const addToCartButton = document.querySelector('.product-details .add-to-cart-button');

  if (checkoutBlockButton instanceof HTMLElement) {
    checkoutBlockButton.style.pointerEvents = 'none';
  }

  function checkValidity() {
    const requiredSelects = optionsContainer.querySelectorAll('select[required]');
    for (let select of requiredSelects) {
      if (select instanceof HTMLSelectElement && !select.disabled && !select.value) return false;
    }

    const radioGroups = optionsContainer.querySelectorAll('fieldset');
    for (let group of radioGroups) {
      const radiosInGroup = group.querySelectorAll('input[type="radio"][required]:not(:disabled)');
      if (radiosInGroup.length > 0) {
        const isChecked = Array.from(radiosInGroup).some(
          (radio) => radio instanceof HTMLInputElement && radio.checked
        );
        if (!isChecked) return false;
      }
    }

    return true;
  }

  const allSelectors = optionsContainer.querySelectorAll('select, input[type="radio"]');
  allSelectors.forEach((el) => {
    el.addEventListener('change', updateButton);
  });

  function updateButton() {
    if (checkoutBlockButton instanceof HTMLElement && checkValidity()) {
      checkoutBlockButton.style.pointerEvents = 'all';
    }
  }

  checkoutBlock.addEventListener('click', () => {
    if (!checkValidity() && addToCartButton instanceof HTMLElement) {
      addToCartButton.click();
    }
  });

  updateButton();
});